import {
  BulkCreateShiftmanagementShiftShiftsInput,
  ShiftManagementAreasViewPageDocument,
  ShiftManagementBulkCreateShiftsDocument,
  ShiftManagementBulkDeleteShiftsDocument,
  ShiftManagementBulkPatchShiftsDocument,
  ShiftManagementMainPageDocument,
} from 'generated/resources/graphql'
import uniq from 'lodash/uniq'

import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import {
  useRefreshShiftsRows,
  useRefreshStats,
} from 'modules/shift_management/hooks/refreshShiftsRows'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { toBreaksPayload } from 'modules/shift_management/lib/buildAddPlannedBreaksParameters'
import buildEmployeeIds from 'modules/shift_management/lib/buildEmployeeIds'
import type PlannedBreakForm from 'modules/shift_management/lib/forms/PlannedBreakForm'
import { trackEditShiftSubmit } from 'modules/shift_management/lib/trackEvents'
import type { ShiftsRow } from 'modules/shift_management/lib/types'
import Shift from 'modules/shift_management/store/internal/models/Shift'

import { BulkPatchProps } from './useEditShifts'
import { useSelectionDispatch } from './useSelection'
import { useShiftsRows } from './useShiftsRows'

interface ReassignShiftProps {
  shifts: Shift[]
  editShiftsParams: BulkPatchProps
  assignShiftsParams: BulkCreateShiftmanagementShiftShiftsInput[]
  isOriginalIncluded: boolean
  plannedBreakForms: PlannedBreakForm[]
  through: 'modal' | 'side_panel'
}

export const useReassignShifts = ({
  shifts,
  editShiftsParams,
  assignShiftsParams,
  isOriginalIncluded,
  plannedBreakForms,
  through,
}: ReassignShiftProps) => {
  const [{ shiftsRows }] = useShiftsRows()
  const [{ view }] = useSchedule()
  const dispatchSelection = useSelectionDispatch()
  const { refreshShiftsRows } = useRefreshShiftsRows()
  const { refreshStats } = useRefreshStats()

  const {
    company: { id: companyId },
  } = useCurrentSession()

  const [bulkDestroyShifts, { loading: loadingDestroyShifts }] = useMutation(
    ShiftManagementBulkDeleteShiftsDocument,
    {
      extractorFn: (data) => data.shiftManagementMutations.bulkDeleteShiftmanagementShift,
    }
  )

  const [bulkPatchShifts, { loading: loadingPatchShifts }] = useMutation(
    ShiftManagementBulkPatchShiftsDocument,
    {
      extractorFn: (data) => data.shiftManagementMutations.bulkPatchShiftmanagementShift,
    }
  )

  const [addShifts, { loading: loadingAddShifts }] = useMutation(
    ShiftManagementBulkCreateShiftsDocument,
    {
      extractorFn: (data) => data.shiftManagementMutations.bulkCreateShiftmanagementShift,
    }
  )

  const onReassignShifts = async (closePortal?: ClosePortalFn) => {
    const assignedToModified = true
    const shiftIds = shifts.map((shift) => shift.id)
    // Get all shift rows that have shifts by employees affected by the edit action to update stats
    const shiftsEmployeeIds = uniq(shifts.map((shift: Shift) => shift.employeeId))
    const relatedShiftsRows = shiftsRows.filter((row: ShiftsRow) => {
      const employeeIds = new Set(row.employeeIds)
      return shiftsEmployeeIds.some((id: number) => employeeIds.has(id))
    })

    const { name, startAt, endAt, notes, extraHours, locationId, workAreaId, mixedValueNotes } =
      editShiftsParams

    // If employee is included edit the corresponding shifts
    if (isOriginalIncluded) {
      await bulkPatchShifts({
        variables: {
          companyId,
          shiftIds,
          startAt,
          endAt,
          notes,
          extraHours,
          locationId,
          workAreaId,
          plannedBreaks: toBreaksPayload(plannedBreakForms),
        },
      })
    } else {
      // If employee is not included destroy corresponding shifts
      await bulkDestroyShifts({
        variables: { ids: shiftIds },
        update: (cache, { data }) => {
          data?.shiftManagementMutations.bulkDeleteShiftmanagementShift.shifts?.forEach((shift) => {
            cache.evict({
              id: cache.identify(shift),
            })
          })
        },
      })
    }

    await refreshShiftsRows(relatedShiftsRows, [])
    const result = await addShifts({
      variables: {
        shifts: assignShiftsParams,
        plannedBreaks: toBreaksPayload(plannedBreakForms),
      },
      // TODO: Actually update the cache properly
      refetchQueries: [ShiftManagementMainPageDocument, ShiftManagementAreasViewPageDocument],
      awaitRefetchQueries: true,
    })

    const handled = handleResult(result, (_errors) => {})
    const involvedEmployeeIds = uniq([
      ...shifts.map((s) => s.employeeId),
      ...assignShiftsParams.map((p) => p.employeeId),
    ])
    refreshShiftsRows([], involvedEmployeeIds)

    if (handled.success) {
      const shiftsData = handled.data?.shifts || []
      const newShifts = shiftsData.map((shift) =>
        Shift.buildFromGraphQL({
          shift,
          employeeFullName: shift.employee?.fullName || '',
          employeeAvatar: shift.employee?.avatar?.url || '',
          employeeLocationId: shift.employee?.locationId || 0,
        })
      )
      const { employeeIds, newShiftsRowsKeys, shiftsReferenceKeys } = buildEmployeeIds(
        view,
        shiftsRows,
        newShifts
      )

      if (newShiftsRowsKeys.length !== 0) {
        await refreshShiftsRows([], employeeIds)
      }

      if (newShiftsRowsKeys.length === 0) {
        // Get shiftsRows where there are affected shifts to update stats
        // This is not necessary when we have added new shiftsRows, as
        // refreshShiftsRows has already fetched the stats
        const previousShiftsRows = shiftsRows.filter((row: ShiftsRow) => {
          return shiftsReferenceKeys.includes(row.key)
        })

        await refreshStats(previousShiftsRows)

        const totalNumberOfAssignedEmployees = isOriginalIncluded
          ? shiftsEmployeeIds.length + assignShiftsParams.length
          : assignShiftsParams.length

        await trackEditShiftSubmit({
          shifts,
          name,
          startAt,
          endAt,
          notes,
          extraHours,
          locationId,
          workAreaId,
          mixedValueNotes,
          view,
          assignedToModified,
          originalEmployeeIncluded: isOriginalIncluded,
          totalNumberOfAssignedEmployees,
          through,
        })

        if (closePortal) closePortal()

        // Clear selection at the end to not remove the edit shifts button,
        // because the sidepanel and modal are rendered from it.
        dispatchSelection({
          type: 'CLEAR_SELECTION',
        })

        app.setFlash({
          type: 'correct',
          message: i18n.t('shift_management.actions.edit_shifts_success'),
        })
      }
    }
  }

  return {
    onReassignShifts,
    submittingOnReassign: loadingDestroyShifts || loadingPatchShifts || loadingAddShifts,
  }
}
